import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import * as Styles from './SearchBusinesses.styles';
import {
  Button, IconAiStar, TextField, IconLoading, IconLocation,
} from 'gatorcomponents';
import { GradientButton } from '@/pages/Studio/components';
import iconMaia from '@/media/onboarding/aiBuilder/AiAnimation.webp';

const SearchBusinesses = ({
  businessesLocale,
  continueButtonClick,
  onGoBack,
  handleChangeInput,
  businessList,
  businessesInputValue,
  setBusinessesInputValue,
  mobile,
}) => {
  const { list, loading } = businessList;

  const [listInputValue, setListInputValue] = useState('');
  const [displayAutoComplete, setDisplayAutoComplete] = useState(false);

  const textFieldWrapperRef = useRef(null);

  const handleGoBack = (isBackButtonTop) => {
    onGoBack && onGoBack(isBackButtonTop);
  };

  const handleContinueButtonClick = () => {
    continueButtonClick && continueButtonClick();
  };

  const handleOnChangeInput = useCallback((e) => {
    setBusinessesInputValue({});
    setListInputValue(e.target.value);
    handleChangeInput(e.target.value);
    if (e.target.value.length > 0) {
      setDisplayAutoComplete(true);
    }
  }, [handleChangeInput, setDisplayAutoComplete]);

  const handleInputFocus = useCallback(() => {
    if (listInputValue.length > 0) {
      setDisplayAutoComplete(true);
    }
  }, [businessesInputValue, setDisplayAutoComplete]);

  const handleSelectAutoCompleteItem = useCallback((item) => {
    setBusinessesInputValue(item);
    setListInputValue(item.location ? `${item.company} | ${item.location}` : item.company);
    setDisplayAutoComplete(false);
  }, [setBusinessesInputValue, setListInputValue, setDisplayAutoComplete]);

  const handleCloseAutoCompleteOnBlur = useCallback(() => {
    function handleClickOutside(event) {
      if (textFieldWrapperRef.current && !textFieldWrapperRef.current.contains(event.target)) {
        setDisplayAutoComplete(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const showAutoComplete = displayAutoComplete && !loading && listInputValue.length > 0 && list.length > 0;

  useEffect(() => {
    handleCloseAutoCompleteOnBlur();
  }, [handleCloseAutoCompleteOnBlur]);

  return (
    <Styles.Wrapper data-testid="ai-builder-create-site-my-business">
      <Styles.TitleAdapter>
        <Styles.Image src={iconMaia} alt="Maia Icon" />
        <Styles.Title>{businessesLocale.title}</Styles.Title>
      </Styles.TitleAdapter>

      <Styles.InputContent>
        <Styles.InputLabel>{businessesLocale.inputLabel}</Styles.InputLabel>

        <Styles.TextFieldWrapper ref={textFieldWrapperRef}>
          <TextField
            testId="my-business-input-field"
            label={businessesLocale.inputPlaceholder}
            value={listInputValue}
            onChange={handleOnChangeInput}
            onFocusInput={handleInputFocus}
            autoComplete="off"
            maxLength={50}
            leadingIcon="IconLocation"
          />

          {loading && (
            <Styles.LoadingWrapper>
              <IconLoading />
            </Styles.LoadingWrapper>
          )}

          {showAutoComplete && (
            <Styles.AutoComplete>
              {list.map(item => (
                <Styles.AutoCompleteItem
                  key={item.company}
                  isSelected={listInputValue === item.company}
                  onClick={() => handleSelectAutoCompleteItem(item)}
                  role="button"
                >
                  <IconLocation size="sm" />
                  <Styles.AutoCompleteItemCompany>
                    <Styles.AutoCompleteItemCompanyName>{item.company}</Styles.AutoCompleteItemCompanyName>
                    <Styles.AutoCompleteItemCompanyLocation>{item.location}</Styles.AutoCompleteItemCompanyLocation>
                  </Styles.AutoCompleteItemCompany>
                </Styles.AutoCompleteItem>
              ))}
            </Styles.AutoComplete>
          )}
        </Styles.TextFieldWrapper>
      </Styles.InputContent>

      <Styles.ButtonWrapper>
        <GradientButton
          testId="my-business-continue-button"
          label={businessesLocale.buttonLabel}
          variant="quinary"
          disabled={!businessesInputValue.company}
          width={mobile ? 'full' : 'fit'}
          onClick={handleContinueButtonClick}
          iconRight={
            <IconAiStar color={!businessesInputValue.company ? 'lowLight' : 'highPure'} size="md" />
          }
        />
      </Styles.ButtonWrapper>

      <Styles.NotFoundCompany>
        {businessesLocale.notFoundCompany.text}
        <Button
          label={businessesLocale.notFoundCompany.linkText}
          variant="text"
          size={mobile ? 'medium' : 'large'}
          testId="my-business-not-found-company-button"
          width="fit"
          onClick={() => handleGoBack(false)}
        />
      </Styles.NotFoundCompany>
    </Styles.Wrapper>
  );
};

export default SearchBusinesses;
